import React, { useEffect, useState } from 'react';
import Toast from '../Common/Toast';
import GeneralSettingsSection from './GeneralSettingsSection';
import SettingsToggleRow from './SettingsToggleRow';
import { useModuleSettings } from '../../contexts/ModuleSettingsContext';
import { getWarehouses } from '../../services/warehousesService';

const AutomatizacionesSettings = () => {
  const {
    receptionConsumeButtonEnabled,
    setReceptionConsumeButtonEnabled,
    receptionConsumeWarehouseIds,
    setReceptionConsumeWarehouseIds,
    receptionConsumeSingleProjectEnabled,
    setReceptionConsumeSingleProjectEnabled,
    loaded,
  } = useModuleSettings();
  const [savingReceptionConsume, setSavingReceptionConsume] = useState(false);
  const [savingSingleProject, setSavingSingleProject] = useState(false);
  const [savingWarehouses, setSavingWarehouses] = useState(false);
  const [loadingWarehouses, setLoadingWarehouses] = useState(false);
  const [warehouses, setWarehouses] = useState([]);
  const [toast, setToast] = useState({ isOpen: false, message: '', type: 'info' });

  useEffect(() => {
    if (!receptionConsumeButtonEnabled) return;

    let cancelled = false;
    setLoadingWarehouses(true);
    getWarehouses({ active_only: true })
      .then((res) => {
        if (cancelled) return;
        if (res.success && Array.isArray(res.data)) {
          setWarehouses(res.data);
        } else {
          setWarehouses([]);
        }
      })
      .catch(() => {
        if (!cancelled) {
          setWarehouses([]);
          setToast({
            isOpen: true,
            message: 'No se pudieron cargar los almacenes',
            type: 'error',
          });
        }
      })
      .finally(() => {
        if (!cancelled) setLoadingWarehouses(false);
      });

    return () => {
      cancelled = true;
    };
  }, [receptionConsumeButtonEnabled]);

  const onToggleReceptionConsume = () => {
    const next = !receptionConsumeButtonEnabled;
    setSavingReceptionConsume(true);
    setReceptionConsumeButtonEnabled(next)
      .then(() => {
        setToast({
          isOpen: true,
          message: 'Automatización guardada',
          type: 'success',
        });
      })
      .catch(() => {
        setToast({
          isOpen: true,
          message: 'No se pudo guardar la automatización',
          type: 'error',
        });
      })
      .finally(() => setSavingReceptionConsume(false));
  };

  const onToggleSingleProject = () => {
    const next = !receptionConsumeSingleProjectEnabled;
    setSavingSingleProject(true);
    setReceptionConsumeSingleProjectEnabled(next)
      .then(() => {
        setToast({
          isOpen: true,
          message: 'Automatización guardada',
          type: 'success',
        });
      })
      .catch(() => {
        setToast({
          isOpen: true,
          message: 'No se pudo guardar la automatización',
          type: 'error',
        });
      })
      .finally(() => setSavingSingleProject(false));
  };

  const onToggleWarehouse = (warehouseId) => {
    const normalizedId = Number(warehouseId);
    const currentIds = receptionConsumeWarehouseIds || [];
    const nextIds = currentIds.includes(normalizedId)
      ? currentIds.filter((id) => id !== normalizedId)
      : [...currentIds, normalizedId];

    setSavingWarehouses(true);
    setReceptionConsumeWarehouseIds(nextIds)
      .then(() => {
        setToast({
          isOpen: true,
          message: 'Almacenes de automatización guardados',
          type: 'success',
        });
      })
      .catch(() => {
        setToast({
          isOpen: true,
          message: 'No se pudieron guardar los almacenes',
          type: 'error',
        });
      })
      .finally(() => setSavingWarehouses(false));
  };

  return (
    <>
      <GeneralSettingsSection
        title="Automatizaciones"
        description="Configura procesos automáticos del sistema."
      >
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
          <div className="px-6 py-4">
            <SettingsToggleRow
              embedded
              title="Botón Consumir en guías de recepción"
              description="Si está activo, en el listado y detalle de guías de recepción aparece el botón Consumir para registrar la salida automática del inventario recibido."
              enabled={receptionConsumeButtonEnabled}
              onToggle={onToggleReceptionConsume}
              disabled={!loaded || savingReceptionConsume}
            />
          </div>

          {receptionConsumeButtonEnabled && (
            <div className="border-t border-gray-200 px-6 py-4">
              <p className="font-medium text-gray-900">Almacenes donde aplica la automatización</p>
              <p className="mt-1 text-sm text-gray-500">
                Selecciona los almacenes de recepción en los que se mostrará el botón Consumir.
              </p>

              {loadingWarehouses ? (
                <p className="mt-4 text-sm text-gray-500">Cargando almacenes...</p>
              ) : warehouses.length === 0 ? (
                <p className="mt-4 text-sm text-gray-500">No hay almacenes activos disponibles.</p>
              ) : (
                <div className="mt-4 grid grid-cols-1 md:grid-cols-2 gap-2">
                  {warehouses.map((warehouse) => {
                    const checked = receptionConsumeWarehouseIds.includes(Number(warehouse.id));
                    return (
                      <label
                        key={warehouse.id}
                        className={`flex items-center gap-3 rounded-lg border px-3 py-2 cursor-pointer transition-colors ${
                          checked ? 'border-teal-300 bg-teal-50' : 'border-gray-200 hover:bg-gray-50'
                        } ${savingWarehouses ? 'opacity-60 pointer-events-none' : ''}`}
                      >
                        <input
                          type="checkbox"
                          checked={checked}
                          onChange={() => onToggleWarehouse(warehouse.id)}
                          disabled={!loaded || savingWarehouses}
                          className="h-4 w-4 rounded border-gray-300 text-teal-600 focus:ring-teal-500"
                        />
                        <span className="min-w-0">
                          <span className="block text-sm font-medium text-gray-900 truncate">
                            {warehouse.code} - {warehouse.name}
                          </span>
                        </span>
                      </label>
                    );
                  })}
                </div>
              )}

              {receptionConsumeButtonEnabled && !loadingWarehouses && receptionConsumeWarehouseIds.length === 0 && (
                <p className="mt-3 text-sm text-amber-700">
                  Debes seleccionar al menos un almacén para que el botón Consumir aparezca.
                </p>
              )}
            </div>
          )}

          {receptionConsumeButtonEnabled && (
            <div className="border-t border-gray-200 px-6 py-4">
              <SettingsToggleRow
                embedded
                title="Todo se consume a un solo proyecto"
                description="Si está activo, al consumir una recepción el modal muestra un único selector de proyecto para todos los productos."
                enabled={receptionConsumeSingleProjectEnabled}
                onToggle={onToggleSingleProject}
                disabled={!loaded || savingSingleProject}
              />
            </div>
          )}
        </div>
      </GeneralSettingsSection>

      <Toast
        isOpen={toast.isOpen}
        message={toast.message}
        type={toast.type}
        onClose={() => setToast((t) => ({ ...t, isOpen: false }))}
      />
    </>
  );
};

export default AutomatizacionesSettings;
